import { ChatAttachment } from '../chat-attachment';
import { Compensations, InjectionMap } from './file-map-response';
import { AiCreditType } from './ai-response';

export interface AiSession {
  session_id: string;
  title: string;
  last_message_preview: string;
  updated_at: string;
}

export interface AiSessionsResponse {
  items: AiSession[];
  next_cursor: string | null;
}

export interface MapArtifactPayload {
  file_name?: string | null;
  maps: InjectionMap[];
  compensations?: Compensations | null;
  errors?: Record<string, { message?: string }>;
}

export interface CorrectionArtifactPayload {
  file_name?: string | null;
  axis_type: string;
  bank: string;
  cells: Array<{
    rpm_lo: number; rpm_hi: number; load_lo: number; load_hi: number;
    lambda_target: number; lambda_real: number; cl_corr_pct: number;
    suggested_corr_pct?: number | null; review?: boolean; lambda_limited?: boolean;
    n: number; corr_std: number;
  }>;
  summary?: { cells_covered?: number; cells_total?: number };
  warnings?: Array<{ message?: string; warning_code?: string } | string>;
}

export interface AiMessage {
  message_id: string;
  role: 'user' | 'assistant';
  content: string;
  content_format: 'plain' | 'markdown';
  meta: any;
  timestamp: string;
  reasoning_content: string | null;
  feedback_rating: number | null;
  dislike_reasons: string[] | null;
  is_guardrail_blocked: boolean | null;
  is_system?: boolean;
  upgradeAction?: boolean;
  /**
   * CTA para a tela de tanques: recarga (perfil bloqueado por `no_balance`, downgrade por
   * `access_limited`) ou troca de tipo de crédito (`credit_type_required` / `credit_type_limited`).
   */
  topUpAction?: boolean;
  ftcfArtifact?: FtcfArtifact;
  ftcfFile?: FtcfFile;
  datalogChart?: DatalogChannelsArtifact;
  /** Structured injection maps and map corrections attached to this turn. */
  mapArtifacts?: MapArtifactPayload[];
  correctionArtifacts?: CorrectionArtifactPayload[];
  /** Payloads crus dos artifacts `chart` do turno; a validação acontece no renderer. */
  charts?: unknown[];
  /** Uma entrada por artifact `drag-run` do turno — o contrato permite mais de uma corrida. */
  dragRuns?: DragRunArtifact[];
  event_type?: string;
  sequence?: number;
  parent_message_id?: string;
  status?: 'pending' | 'answered';
  user_response?: { text: string | null; option_index: number | null } | null;
  clarification_question?: string;
  clarification_options?: string[];
  traceEvents?: TraceEvent[];
  attachments?: ChatAttachment[];
  /** Narração pública de progresso (evento SSE `progress`). Etapas já em pt-BR. */
  progressSteps?: ProgressStep[];
}

/**
 * Etapa curada/sanitizada do evento SSE `progress` (canal público, sempre ligado).
 * Nunca contém dado interno (tool, query, ids, score, modelo, token).
 */
export interface ProgressStep {
  /** id estável do estágio (intent, rag, project, logs, datalog, ...). Não exibir. */
  step: string;
  /** texto pronto pra tela (pt-BR). Único campo visível. */
  label: string;
  /** "start" = ação começou (spinner) · "done" = ação terminou (✓). */
  status: 'start' | 'done';
  /** nº de vezes que a ação ocorreu no turno (ausente = 1). */
  count?: number;
  /** texto curto opcional já formatado, ex.: "3 trechos". */
  detail?: string;
}

export interface TraceEvent {
  type: 'node' | 'route' | 'agent' | 'tool' | 'guardrail' | 'usage' | string;
  ts: number;
  [key: string]: any;
}

/** Texto integral de um item do trace (`GET /AI/sessions/{id}/trace-items?ref=`). TTL de 30 dias no backend. */
export interface AiTraceItem {
  ref: string;
  kind: string;
  chars: number;
  content: string;
  /** O backend corta o conteúdo gravado em 350.000 chars. */
  truncated: boolean;
}

export interface AiSessionDetailSession extends AiSession {
  user_id: string;
  created_at: string;
  mode: string;
  /** Perfil (Eco/Sport/Race) pedido pelo usuário e persistido na sessão; null = nunca escolheu, usa o default do plano. */
  profile: string | null;
}

export interface AiSessionDetail {
  session: AiSessionDetailSession;
  messages: AiMessage[];
  next_cursor: string | null;
}

export interface AiFeedbackRequest {
  session_id: string;
  message_id: string;
  rating: 1 | -1;
  comment?: string;
  dislike_reasons?: string[];
}

export interface AiChatRequest {
  message: string;
  session_id?: string | null;
  /** Perfil (Eco/Sport/Race) escolhido ativamente pelo usuário no seletor. Omitido → o
   *  backend usa o gravado na sessão, senão o default do plano. Máx. 64 chars. */
  profile?: string;
  context?: any;
  tool_results?: any[] | null;
  trace?: boolean;
}

/** Item da vitrine de `GET /AI/profiles`. `name`/`description` são a referência de copy
 *  (pt-BR); o front traduz por `key` e cai neles quando não há tradução local — nunca
 *  esconde um perfil desconhecido. */
export interface AiProfileOption {
  key: string;
  name: string;
  description: string;
  supports_tools: boolean;
  available: boolean;
  /** Motivo de `available: false`. Union aberta: motivo novo do backend ainda renderiza (CTA genérico). */
  locked_reason: 'plan_required' | 'no_balance' | 'credit_type_required' | (string & {}) | null;
}

export interface AiProfilesResponse {
  enabled: boolean;
  /** Perfil da sessão nova deste usuário, já resolvido pelo backend. null = nenhum perfil alcançável (chat indisponível). */
  default: string | null;
  /** Ordem já vem do backend: mais barato ao mais caro. Renderizar como recebido. */
  profiles: AiProfileOption[];
  /** Tipo de crédito atual, que decide a vitrine. Ausente em backend anterior a 2026-10-20. */
  credit_type?: AiCreditType | null;
}

/** Payload do evento SSE `profile` — primeiro evento de conteúdo de todo turno. */
export interface AiProfileEvent {
  requested: string | null;
  /** Única fonte do que rodou de fato no turno — nunca o `requested`. */
  effective: string;
  downgraded?: boolean;
  reason?: 'access_limited' | 'credit_type_limited' | 'not_available' | 'unknown_profile' | (string & {});
  /** Tipo de crédito que paga este turno. */
  credit_type?: AiCreditType | null;
}

export interface AiSseEvent {
  event: string;
  data: any;
}

export interface FtcfNode {
  id: number;
  nodeType: number;
  label: string;
  params: Record<string, any>;
}

export interface FtcfLink {
  fromId: number;
  fromPort: number;
  toId: number;
  toPort: number;
}

export interface FtcfArtifact {
  title: string;
  nodes: FtcfNode[];
  links: FtcfLink[];
}

export interface FtcfFile {
  title: string;
  content: string;
}

export interface DatalogChannelSeries {
  channel: string;
  unit: string | null;
  axis: number;
  data: (number | null)[];
  stats: { min?: number; max?: number; mean?: number; std?: number };
}

/**
 * Motivos conhecidos de `markers.available === false`. NÃO estão documentados em contrato
 * neste repo — foram observados no payload; por isso a union é aberta (ver `reason`).
 */
export type DatalogMarkersReason = 'unavailable' | 'log-not-converted' | 'powerft' | 'map-only';

/**
 * Normaliza o artifact recebido do SSE. O payload entra por cast cru, então listas ausentes
 * (`alerts`/`comments` nulos) chegariam a `.map()` e derrubariam o componente, e `markers`
 * ausente sumiria silenciosamente com a camada de marcadores. Normalizar aqui evita espalhar
 * guardas pelos getters.
 */
export function normalizeDatalogArtifact(payload: unknown): DatalogChannelsArtifact {
  const p = (payload ?? {}) as DatalogChannelsArtifact;
  const m = p.markers as DatalogChannelsArtifact['markers'] | null | undefined;
  return {
    ...p,
    series: p.series ?? [],
    sample_time: p.sample_time ?? [],
    missing_channels: p.missing_channels ?? [],
    ignored_channels: p.ignored_channels ?? [],
    markers: {
      available: m?.available === true,
      reason: m?.reason ?? null,
      launch_offset_s: m?.launch_offset_s ?? null,
      comments: m?.comments ?? [],
      alerts: m?.alerts ?? [],
      truncated: m?.truncated === true,
    },
  };
}

export interface DatalogChannelsArtifact {
  log_id: string;
  file_name: string | null;
  duration_s: number;
  points: number;
  sample_time: (number | null)[];
  series: DatalogChannelSeries[];
  markers: {
    available: boolean;
    /**
     * Motivo da indisponibilidade. Union aberta de propósito: os literais conhecidos dão
     * checagem no build, e `(string & {})` tolera motivo novo do backend sem quebrar o
     * parse — nesse caso a UI usa texto neutro, não mensagem de falha.
     */
    reason: DatalogMarkersReason | (string & {}) | null;
    launch_offset_s: number | null;
    comments: { time: number; text: string }[];
    alerts: { time: number; id: number | null; name: string | null }[];
    truncated: boolean;
  };
  missing_channels: string[];
  ignored_channels: string[];
  downsampled_to: number | null;
}

/* ───────────────────────── artifact `drag-run` (contrato v0.1) ─────────────────────────
 * Análise de uma arrancada. Chega mid-turn, ANTES do texto da IA, e NÃO encerra o turno —
 * mesmo padrão do `datalog-channels`; roteie pelo `type`.                                */

/** Como o backend achou a largada. Diz quão "largada" foi a largada — mostre no cabeçalho. */
export type DragRunT0Rule = 'launch_ok' | 'twostep_release' | 'wot' | 'first_motion';

export type DragRunValidity = 'valid' | 'red_light' | 'aborted_mechanical' | 'test_only' | 'unknown';

/** Fonte da integração que produziu as parciais. Nunca é sensor de pista. */
export type DragRunDistanceSource = 'gps' | 'speed_fr' | 'speed_traction' | 'ecu_distance';

export type DragRunPhaseKind = 'idle' | 'burnout' | 'staging' | 'launch' | 'run' | 'lift' | 'abort';

export type DragRunFlag =
  | 'burnout_detected' | 'multiple_attempts' | 'creep_before_release'
  | 'aborted_run' | 'lift_before_finish' | 'red_light_confirmed';

/**
 * Canais da corrida. Todos têm o mesmo comprimento; `null` é lacuna de gravação e deve
 * INTERROMPER a linha no gráfico, não virar zero. A unidade vai no nome do campo.
 */
export interface DragRunSeries {
  t_s: (number | null)[];
  dist_m: (number | null)[];
  kmh: (number | null)[];
  rpm: (number | null)[];
  map_bar: (number | null)[];
  oil_bar: (number | null)[];
  fuel_bar: (number | null)[];
  battery_v: (number | null)[];
  gear: (number | null)[];
  tps_pct: (number | null)[];
}

/** Ordem fixa dos canais — usada para normalizar e para montar os medidores. */
export const DRAG_RUN_SERIES_KEYS: readonly (keyof DragRunSeries)[] = [
  't_s', 'dist_m', 'kmh', 'rpm', 'map_bar', 'oil_bar', 'fuel_bar', 'battery_v', 'gear', 'tps_pct',
];

export interface DragRunPhase {
  kind: DragRunPhaseKind | (string & {});
  /** Relativo a t0 — negativo antes da largada. */
  t_start_s: number;
  t_end_s: number;
}

/**
 * Uma parcial. `available:false` vem com `reason` e deve renderizar "não atingido" — nunca
 * sumir da tela e nunca virar zero (Seção 7).
 */
export interface DragRunSplit {
  id: string;
  /** Já em pt-BR (`60 pés`, `101 m`, `201 m`). Não recomponha. */
  label: string;
  m: number;
  available: boolean;
  reason?: string | null;
  t_s?: number;
  kmh?: number;
  gear?: number;
  rpm?: number;
  uncertainty_s?: number;
}

/**
 * Valores da ECU no instante da largada. Campo AUSENTE significa canal não gravado, e a UI
 * mostra "sem canal" — por isso nada aqui ganha default numérico na normalização.
 */
export interface DragRunLaunchSnapshot {
  rpm?: number;
  map_bar?: number;
  oil_bar?: number;
  fuel_bar?: number;
  battery_v?: number;
  tps_pct?: number;
  gear?: number;
}

/** `available:false` NÃO significa "não usou 2-step" — significa que não deu para medir. */
export interface DragRunTwoStep {
  available: boolean;
  reason: 'no_launch_in_log' | 'channels_missing' | (string & {}) | null;
  duration_s?: number;
  rpm_hold?: number;
  map_at_release_bar?: number;
  battery_min_v?: number;
}

/** Reação piloto+carro (soltura do 2-step até o carro se mover). Não é o R/T da árvore. */
export interface DragRunReaction {
  available: boolean;
  reason: string | null;
  s?: number;
  definition?: 'twostep_release_to_motion' | (string & {});
}

export interface DragRunHighlight {
  level: 'ok' | 'warn' | 'danger' | (string & {});
  /** Texto pronto do backend. Não gere nem reescreva no front. */
  text: string;
}

export interface DragRunEvent {
  t_s: number;
  kind: 'twostep_arm' | 'release' | 'motion' | 'alert' | 'gear' | 'lift' | 'wot' | (string & {});
  from?: number;
  to?: number;
  id?: number | null;
  /** Já traduzido pelo backend; `null` → "não identificado". */
  name?: string | null;
}

export interface DragRunWarning {
  code: 'estimated_splits' | (string & {});
  msg: string;
}

export interface DragRunInfo {
  /** Instante da largada NO TEMPO DO LOG. Todo o resto do payload é relativo a t0 (t0 = 0). */
  t0_s: number;
  t0_rule: DragRunT0Rule | (string & {});
  /** Rótulo pronto em pt-BR. Não recomponha. */
  t0_label: string;
  end_s: number;
  strip_m: number;
  validity: DragRunValidity | (string & {});
  validity_source: 'detector' | 'user' | null;
  distance_source: DragRunDistanceSource | (string & {});
  uncertainty_s: number;
}

export interface DragRunArtifact {
  log_id: string;
  file_name: string | null;
  duration_s: number;
  sample_hz: number;
  run: DragRunInfo;
  phases: DragRunPhase[];
  splits: DragRunSplit[];
  launch_snapshot: DragRunLaunchSnapshot;
  two_step: DragRunTwoStep;
  reaction: DragRunReaction;
  highlights: DragRunHighlight[];
  flags: (DragRunFlag | (string & {}))[];
  events: DragRunEvent[];
  series: DragRunSeries;
  warnings: DragRunWarning[];
  downsampled_to: number | null;
}

/** Teto de destaques definido no contrato (Seção 3). */
const DRAG_RUN_MAX_HIGHLIGHTS = 5;

/**
 * Normaliza o artifact `drag-run` do SSE, pelos mesmos motivos do `normalizeDatalogArtifact`:
 * o payload entra por cast cru, e uma lista ausente chegaria a `.map()` derrubando o card.
 *
 * Duas garantias do contrato ficam centralizadas aqui, em vez de espalhar guardas pelo
 * template: `two_step`/`reaction` sempre existem (com `available:false` quando não deu para
 * medir, para a Seção 7 poder renderizar o motivo), e uma parcial sem `available` explícito
 * conta como não atingida — o oposto renderizaria tempo ausente como se fosse zero.
 */
export function normalizeDragRunArtifact(payload: unknown): DragRunArtifact {
  const p = (payload ?? {}) as Partial<DragRunArtifact>;
  const run = (p.run ?? {}) as Partial<DragRunInfo>;
  const rawSeries = (p.series ?? {}) as Partial<DragRunSeries>;

  const series = {} as DragRunSeries;
  for (const key of DRAG_RUN_SERIES_KEYS) {
    series[key] = rawSeries[key] ?? [];
  }

  return {
    ...(p as DragRunArtifact),
    run: {
      ...(run as DragRunInfo),
      validity: run.validity ?? 'unknown',
      validity_source: run.validity_source ?? null,
    },
    phases: p.phases ?? [],
    splits: (p.splits ?? []).map((s) => ({ ...s, available: s.available === true })),
    launch_snapshot: p.launch_snapshot ?? {},
    two_step: {
      ...p.two_step,
      available: p.two_step?.available === true,
      reason: p.two_step?.reason ?? null,
    },
    reaction: {
      ...p.reaction,
      available: p.reaction?.available === true,
      reason: p.reaction?.reason ?? null,
    },
    highlights: (p.highlights ?? []).slice(0, DRAG_RUN_MAX_HIGHLIGHTS),
    flags: p.flags ?? [],
    events: p.events ?? [],
    series,
    warnings: p.warnings ?? [],
    downsampled_to: p.downsampled_to ?? null,
  };
}

export interface ClarificationOption {
  label: string;
  description?: string;
  recommended?: boolean;
}

export interface ClarificationQuestion {
  id: string;
  question: string;
  multiSelect?: boolean;
  allowOther?: boolean;
  options?: ClarificationOption[];
}

export interface ClarificationData {
  intro: string;
  questions: ClarificationQuestion[];
}
